<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
    import ModalOverlay from './ModalOverlay.svelte';
    import OverlayBox from './OverlayBox.svelte';
    import { createEventDispatcher } from 'svelte';

    const dispatch = createEventDispatcher();

    // props for OverlayBox
    export let startActions;
    export let endActions;
    export let heading;
    export let headingLevel;
    export let inverted;
    export let overlayVariant;
    export let width;
    export let ariaLabel;

    // props for ModalOverlay
    export let open = false;
    export let disableClosing = false; // prevents closing by clicking outside
    export let disableEscape = false; // prevents closing by escape key

    // DOM elements
    let overlayBoxWrapperElement;
    let previouslyFocused;

    /**
     * @fires open when it opens
     */
    $: if (open) {
        dispatch('open');
    }

    // When ModalOverlayBox opens, focus OverlayBox container
    $: if (open && overlayBoxWrapperElement) {
        previouslyFocused = document && document.activeElement;

        overlayBoxWrapperElement.querySelector('.overlay-box').focus();
    }

    // Note that in this component, width prop is applied only to parent of OverlayBox,
    // instead of passing down to OverlayBox
    const style = width ? `width:${width};` : null;

    /**
     * Close Modal
     * @fires close when it closes
     */
    function close() {
        open = false;
        dispatch('close');

        if (previouslyFocused) {
            previouslyFocused.focus();
        }
    }

    /**
     * Handle OverlayBox event
     * @param {CustomEvent} e
     * @fires action with OverlayBox button key
     */
    function handleAction(e) {
        dispatch('action', { key: e.detail.key });

        if (e.detail.key === 'close') {
            close();
        }
    }

    /**
     * Handle keyboard input:
     * - Escape to close
     * - Tab key focus should be trapped in OverlayBox
     * @param {KeyEvent} e
     */
    function handleKeydown(e) {
        if (!open) {
            return;
        }

        if (e.key === 'Escape' && !disableEscape) {
            close();
            return;
        }

        if (e.key === 'Tab') {
            // trap focus
            const nodes = overlayBoxWrapperElement.querySelectorAll('*');
            const tabbable = Array.from(nodes).filter(n => n.tabIndex !== -1);

            let index = tabbable.indexOf(document.activeElement);
            if (index === -1 && e.shiftKey) index = 0;

            index += tabbable.length + (e.shiftKey ? -1 : 1);
            index %= tabbable.length;

            if (tabbable[index]) {
                tabbable[index].focus();
            }
            e.preventDefault();
        }
    }
</script>

<style>
    .modal-positioning {
        position: relative;
        overflow: auto;
        z-index: var(--layer-5);

        max-width: calc(100% - 4rem);
        max-height: calc(100vh - 4rem);
    }
</style>

{#if open}
    <ModalOverlay {disableClosing} on:close={close} variant={overlayVariant}>
        <div
            class="modal-positioning"
            {style}
            bind:this={overlayBoxWrapperElement}
            on:keydown={handleKeydown}>
            <OverlayBox
                {startActions}
                {endActions}
                {heading}
                {headingLevel}
                {inverted}
                ariaModal={true}
                {ariaLabel}
                on:action={handleAction}>
                <slot />
            </OverlayBox>
        </div>
    </ModalOverlay>
{/if}
